import { useState, type ReactNode } from 'react'
import { Info } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

interface PageInfoProps {
  children: ReactNode
}

/**
 * Inline page/section explainer. Renders a small info icon that,
 * when clicked, expands a collapsible panel with explanatory content.
 *
 * The icon should become a platform-wide byword for "what is this?".
 */
export function PageInfo({ children }: PageInfoProps) {
  const [open, setOpen] = useState(false)

  return (
    <div className="inline-flex flex-col">
      {/* Trigger icon */}
      <Button
        variant="ghost"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex h-5 w-5 items-center justify-center rounded-full border-[0.5px] border-[var(--rule)] text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)] hover:text-[var(--ink-3)]',
          open ? 'opacity-100' : 'opacity-50'
        )}
        aria-expanded={open}
        aria-label="Page information"
      >
        <Info className="h-3 w-3" />
      </Button>
      {/* Collapsible panel */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-200 ease-in-out',
          open ? 'mt-3 max-h-96 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div
          className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-5 py-4 text-base leading-relaxed text-[var(--ink-2)]"
        >
          {children}
        </div>
      </div>
    </div>
  );
}
